import { computed } from "vue";
import {createRouter, createWebHistory} from "vue-router";

import { env } from "config";
import { unauthorized } from "store/portal";

import { checkLogin } from "services/JwtService";

import layout from "layout/layout.vue";

// Defaults
import error404 from "view/error/404";
import error500 from "view/error/500";

// Readme automatically generated
import readmeView from "view/readme/view";

// Dashboard related templates
import dashboardLogin from "view/dashboard/login";
import dashboardReset from "view/dashboard/reset";
import dashboardHome from "view/dashboard/home";
import dashboardView from "view/dashboard/view";

// Specific pages
import dashboardSettings from "view/dashboard/settings";
import dashboardSupport from "view/dashboard/support";
import dashboardMockup from "view/dashboard/mockup";

// Validate visiblity
import { checkVisibility } from "store/view";

export const isCommunity = window.location.pathname.split('/')[1] === "portal";
export const getBaseUrl = () => isCommunity ? "/portal" : "/";

// Function to load children routes based on environment variable
const loadClientRoutes = async () => {
	const subfolder = process.env.VUE_APP_ASSET_SUBFOLDER;
	return await import(`config/routes/${subfolder}.js`).then((mod) => mod.default);
};

const createDynamicRouter = async () => {
	const childrenRoutes = await loadClientRoutes();
	const routes = [
		{
			path: "/",
			component: layout,
			children: [
				{
					path: "",
					name: "Dashboard home",
					component: dashboardHome,
					meta: {
						module: "Dashboard",
						action: "view"
					}
				},
				{ // @TODO extend the page, make it more dynamic and usable
					path: "/profile",
					name: "Dashboard profile",
					component: dashboardView,
					meta: {
						module: "Dashboard",
						action: "profile"
					}
				},
				{ // @TODO give this page a purpose, unused at the moment
					path: "/settings",
					name: "Dashboard settings",
					component: dashboardSettings,
					meta: {
						module: "Dashboard",
						action: "settings"
					}
				},
				{
					path: "/mockup",
					name: "Dashboard mockup",
					component: dashboardMockup,
					meta: {
						module: "Dashboard",
						action: "mockup"
					}
				},
				{
					path: "/support",
					name: "Dashboard support",
					component: dashboardSupport,
					meta: {
						module: "Dashboard",
						action: "support"
					}
				},

				...childrenRoutes,

				{
					path: "/404",
					name: "Dashboard error 404",
					component: error404,
					meta: {
						module: "Dashboard",
						action: "error"
					}
				},
				{
					path: "/500",
					name: "Dashboard error 500",
					component: error500,
					meta: {
						module: "Dashboard",
						action: "error"
					}
				}
			]
		},
		{
			path: "/readme",
			name: "Readme",
			component: readmeView,
			meta: {
				module: "Document",
				action: "view"
			}
		},
		{
			path: "/login",
			name: "Login",
			component: dashboardLogin,
			meta: {
				module: "Dashboard",
				action: "login"
			}
		},
		{
			path: "/reset",
			name: "Reset",
			component: dashboardReset,
			meta: {
				module: "Dashboard",
				action: "reset"
			}
		},
		{
			// the 404 route, when none of the above matches
			path: "/:pathMatch(.*)*",
			redirect: "/404",
		},
	];

	// Create the router
	const router = createRouter({
		saveScrollPosition: false,
		history: createWebHistory(getBaseUrl()),
		base: getBaseUrl(),
		routes
	});

	function checkAuthToken() {
		// Huidige URL parameters ophalen
		const params = new URLSearchParams(window.location.search);

		// Checken of auth_token bestaat en niet leeg is
		const authToken = params.get('auth_token');

		return (authToken && typeof authToken === 'string' && authToken.trim() !== '');
	}

	// Add callback functionality
	router.beforeEach(async (to, from, next) => {
		// Check for verification
		console.log(`Route to: "${to.path}" from: ${from.path}`);
		if (to.path === "/readme") {
			next();
		}
		if (to.path !== "/login" && to.path !== "/forget" && to.path !== '/reset' && from.path !== '/login' && !checkLogin()) {
			if (checkLogin()) {
				return next();
			}
			next("/login");
		}
		if (to.path === '/reset') {
			setTimeout(() => {
				if (checkAuthToken()) {
					next();
				}
				next("/forget");
			});
		}
		if (to.path === "/login") {
			next();
		}

		// Validate visibility
		if (to.path === "/") {
			next();
		} else {
			checkVisibility(to.name).then((visible) => {
				if (!visible && (to.name === "Dashboard configuration company")) {
					return next("/referral");
				}
				visible ? next() : next("/");
			});
		}

		// Scroll page to top on every route change
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 100);
		next();
	});

	return router;
};

export default createDynamicRouter;